import { PaperAirplaneIcon, SparklesIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import { useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { describeApiError } from '../../data/http';
import { runOutlineChat, type ChatTurn, type OutlineNode } from '../../data/outline';
import { Spinner } from '../ui/Spinner';

interface OutlineChatProps {
  onOutlineChange: (outline: OutlineNode[]) => void;
  context: Record<string, string>;
}

const MAX_TEXTAREA_HEIGHT_PX = 144;

/** A lightweight, multi-turn chat: each reply can update the outline directly, not just talk about it. */
export function OutlineChat({ onOutlineChange, context }: OutlineChatProps) {
  const { t } = useTranslation();

  const [messages, setMessages] = useState<ChatTurn[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Grows the textarea with its content (capped), and shrinks it back once the input is cleared.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT_PX)}px`;
  }, [input]);

  useLayoutEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, busy]);

  const send = async (overrideText?: string) => {
    const text = (overrideText ?? input).trim();
    if (!text || busy) return;

    const turns: ChatTurn[] = [...messages, { role: 'user', content: text }];
    setMessages(turns);
    setInput('');
    setBusy(true);
    setError(null);

    try {
      const result = await runOutlineChat(turns, context);
      setMessages([...turns, { role: 'assistant', content: result.message, choices: result.choices }]);
      if (result.outline.length > 0) onOutlineChange(result.outline);
    } catch (err) {
      setError(describeApiError(err, t('outline.chat.failed')));
    } finally {
      setBusy(false);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void send();
    }
  };

  // Choices are only ever offered against the most recent assistant turn — once superseded, they're stale.
  const lastMessage = messages[messages.length - 1];
  const activeChoices = !busy && lastMessage?.role === 'assistant' ? (lastMessage.choices ?? []) : [];

  return (
    <div className="flex h-96 flex-col rounded-lg border border-border bg-surface">
      <div className="flex-1 space-y-3 overflow-y-auto p-3" aria-live="polite">
        {messages.length === 0 ? (
          <MessageBubble role="assistant" content={t('outline.chat.empty')} />
        ) : (
          messages.map((message, index) => <MessageBubble key={index} role={message.role} content={message.content} />)
        )}

        {activeChoices.length > 0 ? (
          <div className="ml-10 flex flex-wrap gap-2" role="group" aria-label={t('outline.chat.choicesLabel')}>
            {activeChoices.map((choice) => (
              <button
                key={choice}
                type="button"
                onClick={() => void send(choice)}
                className="rounded-full border border-input bg-surface px-3 py-1.5 text-sm font-medium text-foreground transition hover:bg-muted"
              >
                {choice}
              </button>
            ))}
          </div>
        ) : null}

        {busy ? (
          <div className="flex items-end gap-2">
            <ChatAvatar role="assistant" />
            <div className="flex items-center gap-1 rounded-lg bg-muted px-3 py-2.5">
              <span className="sr-only">{t('outline.chat.thinking')}</span>
              <span className="size-1.5 animate-bounce rounded-full bg-muted-foreground [animation-delay:-0.3s]" />
              <span className="size-1.5 animate-bounce rounded-full bg-muted-foreground [animation-delay:-0.15s]" />
              <span className="size-1.5 animate-bounce rounded-full bg-muted-foreground" />
            </div>
          </div>
        ) : null}

        <div ref={messagesEndRef} />
      </div>

      {error ? <p className="px-3 pb-1 text-xs text-destructive">{error}</p> : null}

      <form
        onSubmit={(event) => {
          event.preventDefault();
          void send();
        }}
        className="flex items-end gap-2 border-t border-border p-3"
      >
        <textarea
          ref={textareaRef}
          rows={1}
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('outline.chat.placeholder')}
          aria-label={t('outline.chat.inputLabel')}
          disabled={busy}
          className="max-h-36 flex-1 resize-none overflow-y-auto rounded-sm border border-input bg-surface px-3 py-2 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22 disabled:opacity-60"
        />
        <button
          type="submit"
          disabled={busy || !input.trim()}
          aria-label={t('outline.chat.send')}
          className="inline-flex items-center justify-center rounded bg-primary p-2 text-primary-foreground transition hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? <Spinner className="size-4" /> : <PaperAirplaneIcon className="size-4" aria-hidden="true" />}
        </button>
      </form>
    </div>
  );
}

function MessageBubble({ role, content }: { role: ChatTurn['role']; content: string }) {
  return (
    <div className={`flex items-end gap-2 ${role === 'user' ? 'flex-row-reverse' : ''}`}>
      <ChatAvatar role={role} />
      <p
        className={`max-w-[75%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
          role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'
        }`}
      >
        {content}
      </p>
    </div>
  );
}

function ChatAvatar({ role }: { role: ChatTurn['role'] }) {
  const { t } = useTranslation();
  const isUser = role === 'user';

  return (
    <span
      role="img"
      aria-label={t(isUser ? 'outline.chat.youLabel' : 'outline.chat.aiLabel')}
      className={`grid size-8 shrink-0 place-items-center rounded-full ${
        isUser ? 'bg-muted text-muted-foreground' : 'bg-accent text-accent-foreground'
      }`}
    >
      {isUser ? (
        <UserCircleIcon className="size-5" aria-hidden="true" />
      ) : (
        <SparklesIcon className="size-5" aria-hidden="true" />
      )}
    </span>
  );
}
